import { Button, Layout } from "antd";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MobileNavbar from "src/components/utils/MobileNavbar";
import { StyledRadioGroup } from "src/components/utils/RadioGroup";
import {
  bathroomOptions,
  bedroomOptions,
  cleaningTypes,
} from "src/constants/options";
import { resolveTypeId } from "src/lib/price";
import { VerifyCoupon } from "src/graphql/mutations/coupons";
import { useResizeObserver } from "src/hooks/useResizeObserver";
import { useAreaCalendar } from "src/hooks/useAreaCalendar";
import { ZipField } from "src/components/utils/ZipField";
import { isZip } from "src/lib/proximity";
import { useMutation } from "urql";
import {
  setBathrooms,
  setBedrooms,
  setOccurance,
  setPromocode,
  setTypes,
  setZip,
  setZipEditorOpen,
} from "../actions";
import { logEvent } from "../analytics/amplitude";
import { openNotificationWithIcon } from "src/components/utils/OpenNotificationWithIcon";

const Bedrooms = ({ dispatch, bedrooms }) => {
  return (
    <Fragment>
      <StyledRadioGroup
        size="large"
        id="bedrooms"
        name="bedrooms"
        label="Number of Bedrooms"
        defaultValue={bedrooms}
        style={{ display: "flex" }}
        options={bedroomOptions}
        value={bedrooms}
        onChange={(e) => dispatch(setBedrooms(e.target.value))}
      />
    </Fragment>
  );
};

const Bathrooms = ({ dispatch, bathrooms }) => {
  return (
    <Fragment>
      <StyledRadioGroup
        size="large"
        id="bathrooms"
        name="bathrooms"
        label="Number of Bathrooms"
        defaultValue={String(bathrooms)}
        options={bathroomOptions}
        value={String(bathrooms)}
        style={{ display: "flex" }}
        onChange={(e) => dispatch(setBathrooms(e.target.value))}
      />
    </Fragment>
  );
};

const CleaningTypes = ({ dispatch, types, isMobile }) => (
  <Fragment>
    <StyledRadioGroup
      id="cleaningType"
      name="cleaningType"
      size="large"
      defaultValue={types}
      options={cleaningTypes}
      label="Type of cleaning"
      value={types}
      style={
        isMobile
          ? {
              flexWrap: "wrap",
            }
          : {}
      }
      onChange={(e) => dispatch(setTypes(e.target.value))}
    />
  </Fragment>
);

const CLEANING_TYPE_DESCRIPTIONS = {
  standard:
    "Our basic clean. We cover all Checklist items. Extras optional. Best for those who clean regularly.",
  special_mission:
    "Our most popular. We cover all checklist items with extra time. Best for homes that haven’t been cleaned in a while.",
  super_clean:
    "We’ve got work to do. We cover all Checklist items, plus our extras. Best for those who rarely clean.",
  move_in_out:
    "Moving in / out can be a hassle. We cover all Checklist items, plus our extras. Best for those who are moving in / out.",
};

const MemoizedBedrooms = React.memo(Bedrooms);
const MemoizedBathrooms = React.memo(Bathrooms);
const MemoizedCleaningTypes = React.memo(CleaningTypes);

export default function StepOne(props) {
  const dispatch = useDispatch();
  const { bedrooms, bathrooms, types, zip, zipFromLink } = useSelector(
    // @ts-ignore
    (state) => state.bookingReducer
  );
  const { calendar } = useAreaCalendar(zip);
  const [zipInvalid, setZipInvalid] = useState(false);

  const [, verifyCoupon] = useMutation(VerifyCoupon);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("d");
    if (!code || code == null) {
      return;
    }
    verifyCoupon({ code }).then((result) => {
      if (result.error || !result.data) {
        logEvent("User entered an invalid promocode", { promocode: code });
        openNotificationWithIcon("error", "Invalid promocode", "");
        dispatch(setPromocode("", 0));
      } else {
        dispatch(setOccurance(0));
        logEvent("User applied promocode", { promocode: code });
        openNotificationWithIcon(
          "success",
          "Promocode applied",
          "You just availed a discount of $" +
            result.data.verifyCoupon?.discount +
            "!"
        );
        dispatch(setPromocode(code, result.data?.verifyCoupon?.discount));
      }
    });
  }, [dispatch, verifyCoupon]);

  const width = useResizeObserver();

  const isMobile = width <= 500;

  /** Pressed Next while the ZIP was still being checked: go on once it answers. */
  const [nextWhenChecked, setNextWhenChecked] = useState(false);

  /**
   * Brings the ZIP field into view and focus — on a phone the error was off
   * screen. A customer who arrived with a ZIP has no field here; theirs is in
   * the header chip, so that opens instead.
   */
  const showZipField = () => {
    if (zipFromLink) {
      dispatch(setZipEditorOpen(true));
      return;
    }
    const field = document.getElementById("zip");
    field?.scrollIntoView({ block: "center", behavior: "smooth" });
    field?.focus({ preventScroll: true });
  };

  const nextAction = useCallback(() => {
    /**
     * A ZIP we can use, or no step 2. Refused outright only when the service
     * area said no; "could not check" lets the customer through, since the
     * address step and checkout both check again. Still checking: wait for the
     * answer rather than let a ZIP we do not serve through unchecked.
     */
    if (!isZip(zip)) {
      setZipInvalid(true);
      showZipField();
      return;
    }
    if (calendar === null) {
      setNextWhenChecked(true);
      return;
    }
    if (calendar.status === "refused") {
      showZipField();
      return;
    }
    logEvent("User selected bedrooms, bathrooms and cleaning type...", {
      bedrooms,
      bathrooms,
    });
    // @ts-ignore
    props.changePage(2);
  }, [bathrooms, bedrooms, props, zip, calendar, zipFromLink]);

  useEffect(() => {
    if (!nextWhenChecked || calendar === null) return;
    setNextWhenChecked(false);
    nextAction();
  }, [nextWhenChecked, calendar, nextAction]);

  return (
    <Layout className="step-layout mobile-step-layout">
      <h1 className="semi-bold">Book a cleaning today</h1>
      <br />
      {/* Arrived with a ZIP: it is in the header chip (`ZipChip`) instead. */}
      {zipFromLink ? null : (
        <>
          <ZipField
            zip={zip}
            calendar={calendar}
            invalid={zipInvalid && !isZip(zip)}
            onChange={(value) => {
              setZipInvalid(false);
              setNextWhenChecked(false);
              dispatch(setZip(value));
            }}
            onEnter={nextAction}
          />
          <br />
        </>
      )}
      <MemoizedBedrooms dispatch={dispatch} bedrooms={bedrooms} />
      <br />
      <MemoizedBathrooms dispatch={dispatch} bathrooms={bathrooms} />
      <br />
      <MemoizedCleaningTypes
        isMobile={isMobile}
        dispatch={dispatch}
        types={types}
      />
      <br />
      <span className="center mt-2">
        {CLEANING_TYPE_DESCRIPTIONS[resolveTypeId(types)]}
      </span>
      <br />
      {isMobile ? (
        <MobileNavbar nextAction={nextAction} />
      ) : (
        <Button onClick={nextAction} type="primary" className="next-button">
          Next
        </Button>
      )}
      <br />
    </Layout>
  );
}
